import React from 'react';
import { format } from 'date-fns';
import { FileText, BookOpen, BookMarked, Target, Eye, Upload, MessageSquare, Paperclip } from 'lucide-react';

const TaskCard = ({
  task,
  getStatusColor,
  getTaskStatusDisplay,
  handleOpenSubmitModal,
  onViewStudentTaskDetail,
  showActions,
  showPoints,
  onEditTask,
  onDeleteTask,
  onViewTask,
  onAssignTask,
  onToggleVisibility,
  docenteView = false
}) => {
  const hasSubmission = task.submission_path &&
    (Array.isArray(task.submission_path) ? task.submission_path.length > 0 : true);

  const estadoNormalizado = task.estado?.toString().trim().toUpperCase();

  const puedeSubirEntrega = (estadoNormalizado === 'ASIGNADA' || estadoNormalizado === 'VENCIDA') &&
    !hasSubmission &&
    !docenteView;

  const recursosDocente = docenteView ? (task.recursos || []) : (task.TareaMaestra?.recursos || []);

  return (
    <div className="bg-slate-800/60 backdrop-blur-lg p-5 rounded-xl shadow-lg mb-4 border border-slate-700/60 hover:border-slate-600 transition-all">
      <div className="flex justify-between items-start gap-2 mb-2">
        <h3 className="text-lg font-bold text-purple-300">
          {docenteView ? task.titulo : task.TareaMaestra?.titulo}
        </h3>
        <span className={`px-2.5 py-1 text-xs leading-5 font-semibold rounded-full border ${getStatusColor(task.estado)}`}>
          {getTaskStatusDisplay(task)}
        </span>
      </div>

      <div
        className="text-sm text-slate-300 mb-3 line-clamp-3 prose prose-sm prose-invert max-w-none"
        dangerouslySetInnerHTML={{ __html: docenteView ? task.descripcion : task.TareaMaestra?.descripcion }}
      />

      <div className="flex flex-col space-y-1.5 text-sm text-slate-400 mt-2">
        <div className="flex items-center gap-2">
          <BookOpen size={15} className="text-emerald-400 flex-shrink-0" />
          <span><strong>Cátedra:</strong> {docenteView ? task.Catedra?.nombre : task.TareaMaestra?.Catedra?.nombre}</span>
        </div>
        {(docenteView ? task.UnidadPlan?.PlanDeClases?.titulo : task.TareaMaestra?.UnidadPlan?.PlanDeClases?.titulo) && (
          <div className="flex items-center gap-2">
            <BookMarked size={15} className="text-blue-400 flex-shrink-0" />
            <span><strong>Plan:</strong> {docenteView ? task.UnidadPlan.PlanDeClases.titulo : task.TareaMaestra.UnidadPlan.PlanDeClases.titulo}</span>
          </div>
        )}
        {(docenteView ? task.UnidadPlan?.periodo : task.TareaMaestra?.UnidadPlan?.periodo) && (
          <div className="flex items-center gap-2">
            <Target size={15} className="text-purple-400 flex-shrink-0" />
            <span><strong>Unidad:</strong> {docenteView ? task.UnidadPlan.periodo : task.TareaMaestra.UnidadPlan.periodo}</span>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 mt-3 pt-3 border-t border-slate-700/50 text-xs text-slate-300">
        <span><strong>Puntos Posibles:</strong> {docenteView ? task.puntos_posibles : task.TareaMaestra?.puntos_posibles}</span>
        <span>•</span>
        <span>
          <strong>Vence:</strong>{' '}
          {(docenteView ? task.fecha_entrega : task.TareaMaestra?.fecha_entrega)
            ? format(new Date(docenteView ? task.fecha_entrega : task.TareaMaestra.fecha_entrega), 'dd/MM/yyyy')
            : 'N/A'}
        </span>
        {recursosDocente.length > 0 && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-blue-500/15 text-blue-300 border border-blue-500/30">
            <Paperclip size={12} /> {recursosDocente.length} recurso(s)
          </span>
        )}
        {!docenteView && hasSubmission && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-purple-500/15 text-purple-300 border border-purple-500/30">
            <FileText size={12} /> {Array.isArray(task.submission_path) ? task.submission_path.length : 1} entregado(s)
          </span>
        )}
      </div>

      {showPoints && task.puntos_obtenidos !== null && task.puntos_obtenidos !== undefined && (
        <div className="mt-3 p-3 rounded-lg bg-emerald-950/40 border border-emerald-500/30">
          <p className="text-sm font-bold text-emerald-400">
            Calificación Obtenida: {task.puntos_obtenidos} / {task.TareaMaestra?.puntos_posibles} pts
          </p>
          {task.comentario_docente && (
            <p className="text-xs text-slate-300 mt-1 flex items-start gap-1.5">
              <MessageSquare size={14} className="text-emerald-400 flex-shrink-0 mt-0.5" />
              <span><strong>Devolución del docente:</strong> {task.comentario_docente}</span>
            </p>
          )}
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        {!docenteView && onViewStudentTaskDetail && (
          <button
            onClick={() => onViewStudentTaskDetail(task)}
            className="inline-flex items-center gap-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs font-semibold py-2 px-3 rounded-lg transition-colors"
          >
            <Eye size={14} />
            Ver Detalle
          </button>
        )}

        {showActions && puedeSubirEntrega && (
          <button
            onClick={() => handleOpenSubmitModal(task)}
            className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold py-2 px-3 rounded-lg transition-colors"
          >
            <Upload size={14} />
            Subir Entrega
          </button>
        )}

        {showActions && docenteView && (
          <>
            <button
              onClick={() => onViewTask(task)}
              className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold py-2 px-3 rounded-lg"
            >
              Ver
            </button>
            <button
              onClick={() => onEditTask(task)}
              className="bg-yellow-600 hover:bg-yellow-700 text-white text-xs font-bold py-2 px-3 rounded-lg"
            >
              Editar
            </button>
            <button
              onClick={() => onDeleteTask(task.id)}
              className="bg-red-600 hover:bg-red-700 text-white text-xs font-bold py-2 px-3 rounded-lg"
            >
              Eliminar
            </button>
            <button
              onClick={() => onAssignTask(task)}
              className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold py-2 px-3 rounded-lg"
            >
              Asignar
            </button>
            {task.publicacionId && (
              <button
                onClick={() => onToggleVisibility(task.publicacionId, task.catedraId)}
                className={`${task.visibleToStudents ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'} text-white text-xs font-bold py-2 px-3 rounded-lg`}
              >
                {task.visibleToStudents ? 'Ocultar en Tablón' : 'Publicar en Tablón'}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TaskCard;
